import React from 'react';
import styled from 'styled-components';
import { t } from '@lingui/macro';
import { DataAmount, ToolTipText, Tooltip } from '@toqio/toqio-web-components';
import { formatAmount } from './styles';
import { InfoIcon } from './icons';

/**
 * List aggregate counters (PRD §4c/§12): Total payments (`total`) and
 * Total amount (`totalAmount` + currency), each with an info tooltip
 * (MAPPINGS [Tooltip]).
 *
 * Uses the DS `DataAmount` (label + value + currency suffix) like lum-web's
 * `multiAccountHeader`, so the label/value/currency typography and spacing come
 * from the DS. The info icon is the inline `InfoIcon` (the DS exports no
 * `DataAmountIcon`), passed via `customIcon` and wrapped in a DS `Tooltip`.
 */
const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 48px;
`;

const tooltipCopy = () => t`Only successful payments in the selected period are included.`;

const InfoTooltip: React.FC = () => (
  <Tooltip position="top" content={<ToolTipText>{tooltipCopy()}</ToolTipText>}>
    <InfoIcon />
  </Tooltip>
);

type Props = {
  total: number;
  totalAmount: number;
  currency: string;
  locale: string;
};

export const AggregateCounters: React.FC<Props> = ({
  total,
  totalAmount,
  currency,
  locale,
}) => (
  <Row>
    <DataAmount
      label={t`Total payments`}
      value={new Intl.NumberFormat(locale).format(total)}
      customIcon={<InfoTooltip />}
    />
    <DataAmount
      label={t`Total amount`}
      value={formatAmount(totalAmount, locale)}
      currency={currency}
      customIcon={<InfoTooltip />}
    />
  </Row>
);
